import { Navigate, Route, Routes } from 'react-router';

import { AppShell } from './app/AppShell';
import { PortalShell } from './app/PortalShell';

/* ---- Authentication ---------------------------------------------------- */
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { LoginPage } from './pages/LoginPage';

/* ---- Platform ---------------------------------------------------------- */
import { ContractorsPage } from './pages/ContractorsPage';
import { DashboardPage } from './pages/DashboardPage';
import { EvidencePage } from './pages/EvidencePage';
import { FinancialsPage } from './pages/FinancialsPage';
import { InvestorDetailPage } from './pages/InvestorDetailPage';
import { InvestorsPage } from './pages/InvestorsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OpportunitiesPage } from './pages/OpportunitiesPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { ProjectEvidencePage } from './pages/ProjectEvidencePage';
import { ProjectFinancialsPage } from './pages/ProjectFinancialsPage';
import { ProjectInvestorsPage } from './pages/ProjectInvestorsPage';
import { ProjectOverviewPage } from './pages/ProjectOverviewPage';
import { ProjectPublicationsPage } from './pages/ProjectPublicationsPage';
import { ProjectSchedulePage } from './pages/ProjectSchedulePage';
import { ProjectShell } from './pages/ProjectShell';
import { ProjectsPage } from './pages/ProjectsPage';
import { PublicationsPage } from './pages/PublicationsPage';
import { ReportDetailPage } from './pages/ReportDetailPage';
import { ReportsPage } from './pages/ReportsPage';
import { SchedulePage } from './pages/SchedulePage';

/* ---- Investor portal --------------------------------------------------- */
import { PortalDocumentsPage } from './pages/portal/PortalDocumentsPage';
import { PortalHomePage } from './pages/portal/PortalHomePage';
import { PortalOpportunitiesPage } from './pages/portal/PortalOpportunitiesPage';
import { PortalProjectPage } from './pages/portal/PortalProjectPage';
import { PortalUpdateDetailPage } from './pages/portal/PortalUpdateDetailPage';
import { PortalUpdatesPage } from './pages/portal/PortalUpdatesPage';

/**
 * The route table.
 *
 * The two products are mounted as two sibling shells rather than one shell with
 * conditional navigation. That is a structural decision, not a styling one: the
 * platform's shell knows about the internal navigation and the notification
 * queue, and the portal's does not. Sharing a shell would mean every
 * investor-facing route passed through code that can render internal sections —
 * which is exactly the coupling the document's permission model exists to
 * prevent.
 *
 * Every project-scoped screen nests under `ProjectShell`, so the development's
 * identity, phase and progress are resolved and laid out once, and each tab
 * renders only its own content.
 */
export function App() {
  return (
    <Routes>
      {/*
        The sign-in screen IS the entry point.

        `/` is what a visitor meets first, and the platform now lives behind it at
        `/dashboard` — a mockup that opened straight onto the dashboard never
        showed the screen the design exists for, and gave no way in that read as
        a way in.
      */}
      <Route path="/" element={<LoginPage />} />

      {/*
        `/login` is kept as a redirect rather than deleted: it is the path a
        bookmark, a sign-out or an older shared link will carry, and a dead end
        there would look like a broken build rather than a moved page.
      */}
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/*
        Where the provider round trip lands, outside BOTH shells.

        It is a redirect target rather than a screen anyone navigates to, and it
        runs before a session exists — inside `AppShell` it would be caught by
        the shell's own signed-out redirect, which is the one route that must
        never bounce.
      */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* ---- Investor portal ------------------------------------------- */}
      <Route path="/portal" element={<PortalShell />}>
        <Route index element={<PortalHomePage />} />
        <Route path="projects/:projectId" element={<PortalProjectPage />} />
        <Route path="updates" element={<PortalUpdatesPage />} />
        <Route path="updates/:publicationId" element={<PortalUpdateDetailPage />} />
        <Route path="documents" element={<PortalDocumentsPage />} />
        <Route path="opportunities" element={<PortalOpportunitiesPage />} />
      </Route>

      {/* ---- Internal platform ----------------------------------------- */}
      <Route element={<AppShell />}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="opportunities" element={<OpportunitiesPage />} />

        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectShell />}>
          <Route index element={<ProjectOverviewPage />} />
          <Route path="schedule" element={<ProjectSchedulePage />} />
          <Route path="financials" element={<ProjectFinancialsPage />} />
          <Route path="evidence" element={<ProjectEvidencePage />} />
          <Route path="investors" element={<ProjectInvestorsPage />} />
          <Route path="publications" element={<ProjectPublicationsPage />} />
        </Route>

        <Route path="schedule" element={<SchedulePage />} />
        <Route path="evidence" element={<EvidencePage />} />
        <Route path="financials" element={<FinancialsPage />} />
        <Route path="contractors" element={<ContractorsPage />} />

        <Route path="investors" element={<InvestorsPage />} />
        <Route path="investors/:investorId" element={<InvestorDetailPage />} />

        <Route path="publications" element={<PublicationsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/:reportId" element={<ReportDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="permissions" element={<PermissionsPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/*
         Anything outside both shells lands on the portal's first screen rather
         than a bare 404 — an investor following a stale link should arrive at
         their own dashboard, not at a dead end.
      */}
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}
